import React, { useEffect, useMemo, useState } from "react";
import {
  FaBookOpen,
  FaExclamationTriangle,
  FaLayerGroup,
  FaRunning,
  FaUsers,
} from "react-icons/fa";

import DisciplineCard from "./DisciplineCard";
import Reveal from "../../../components/Reveal/Reveal";
import { getAllDisciplines } from "../../../services/disciplinesServices";

const SkeletonCard = () => (
  <div className="overflow-hidden rounded-[2rem] border border-slate-200 bg-white shadow-sm dark:border-white/10 dark:bg-slate-900">
    <div className="h-56 animate-pulse bg-slate-200 dark:bg-slate-800" />
    <div className="space-y-4 p-5">
      <div className="h-5 w-2/3 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-full animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      <div className="h-4 w-4/5 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
    </div>
  </div>
);

const EmptyState = () => (
  <div className="col-span-full rounded-[2rem] border border-dashed border-slate-300 bg-white/70 p-10 text-center shadow-sm dark:border-white/10 dark:bg-white/5">
    <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-orange-100 text-orange-600 dark:text-orange-400 dark:bg-orange-500/10">
      <FaLayerGroup />
    </div>

    <h3 className="font-barlow text-2xl font-bold text-slate-950 dark:text-white">
      Aucune discipline disponible
    </h3>

    <p className="mx-auto mt-3 max-w-xl text-sm leading-6 text-slate-500 dark:text-slate-400">
      Les disciplines du club apparaîtront ici dès qu’elles seront ajoutées dans
      l’espace d’administration.
    </p>
  </div>
);

const ErrorState = ({ message }) => (
  <div className="mb-8 rounded-[1.5rem] border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
    <div className="flex items-center justify-center gap-3">
      <FaExclamationTriangle />
      {message}
    </div>
  </div>
);

const isActiveDiscipline = (discipline) => {
  if (
    discipline?.is_active === undefined ||
    discipline?.is_active === null ||
    discipline?.is_active === ""
  ) {
    return true;
  }

  return (
    discipline.is_active === true ||
    discipline.is_active === 1 ||
    discipline.is_active === "1"
  );
};

const keyFor = (discipline, index) =>
  discipline?.id ||
  discipline?.slug ||
  `${discipline?.nom_discipline || "discipline"}-${index}`;

export default function Disciplines() {
  const [disciplines, setDisciplines] = useState([]);
  const [loading, setLoading] = useState(true);
  const [errorMsg, setErrorMsg] = useState("");

  useEffect(() => {
    let mounted = true;

    const fetchDisciplines = async () => {
      try {
        setLoading(true);
        setErrorMsg("");

        const data = await getAllDisciplines();

        if (!mounted) return;

        setDisciplines(Array.isArray(data) ? data : []);
      } catch (error) {
        console.error(
          "Erreur lors de la récupération des disciplines :",
          error,
        );

        if (mounted) {
          setErrorMsg("Impossible de charger les disciplines.");
        }
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchDisciplines();

    return () => {
      mounted = false;
    };
  }, []);

  const visibleDisciplines = useMemo(() => {
    return disciplines.filter(isActiveDiscipline);
  }, [disciplines]);

  return (
    <section className="relative overflow-hidden bg-slate-50 py-20 text-slate-950 dark:bg-slate-950 dark:text-white lg:py-28">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,rgba(242,113,49,0.13),transparent_34%),radial-gradient(circle_at_bottom_right,rgba(59,130,246,0.10),transparent_32%)]" />

      <div className="absolute inset-0 bg-[linear-gradient(to_right,rgba(148,163,184,0.14)_1px,transparent_1px),linear-gradient(to_bottom,rgba(148,163,184,0.14)_1px,transparent_1px)] bg-[size:42px_42px] opacity-40 dark:opacity-20" />

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <Reveal className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-orange-200 bg-orange-50 px-4 py-2 text-xs font-bold uppercase tracking-[0.22em] text-orange-700 shadow-sm dark:border-orange-500/20 dark:bg-orange-500/10 dark:text-orange-300">
            <FaRunning />
            Activités du club
          </div>

          <h2 className="mt-6 font-barlow text-4xl font-bold tracking-tight text-slate-950 dark:text-white sm:text-5xl lg:text-6xl">
            Nos{" "}
            <span className="text-orange-600 dark:text-orange-400">
              disciplines
            </span>
          </h2>

          <p className="mx-auto mt-5 max-w-2xl text-base leading-8 text-slate-600 dark:text-slate-300">
            Découvrez les activités proposées par le club, les publics concernés
            et l’esprit de chaque pratique.
          </p>
        </Reveal>

        <Reveal
          delay={100}
          className="mx-auto mt-10 grid max-w-4xl gap-4 sm:grid-cols-3"
        >
          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaLayerGroup className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              {visibleDisciplines.length}
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Discipline{visibleDisciplines.length > 1 ? "s" : ""}
            </p>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaUsers className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              Tous
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Publics
            </p>
          </div>

          <div className="rounded-[1.7rem] border border-slate-200 bg-white/80 p-5 text-center shadow-sm backdrop-blur-xl dark:border-white/10 dark:bg-white/5">
            <FaBookOpen className="mx-auto mb-3 text-2xl text-orange-600 dark:text-orange-400" />
            <p className="font-barlow text-3xl font-bold text-slate-950 dark:text-white">
              Club
            </p>
            <p className="mt-1 text-xs font-bold uppercase tracking-[0.18em] text-slate-400">
              Encadrement
            </p>
          </div>
        </Reveal>

        {errorMsg && <ErrorState message={errorMsg} />}

        <div
          className="
    mt-14 grid justify-center gap-6
    grid-cols-[repeat(auto-fit,minmax(280px,320px))]
  "
        >
          {loading &&
            Array.from({ length: 8 }).map((_, index) => (
              <SkeletonCard key={`discipline-skeleton-${index}`} />
            ))}

          {!loading && visibleDisciplines.length === 0 && <EmptyState />}

          {!loading &&
            visibleDisciplines.map((discipline, index) => (
              <Reveal
                key={keyFor(discipline, index)}
                variant="zoom"
                delay={index * 80}
                className="w-full"
              >
                <DisciplineCard
                  title={discipline?.nom_discipline}
                  ageRange={discipline?.description_discipline}
                  imageSrc={discipline?.image_discipline}
                  textDiscipline={discipline?.contenu_discipline}
                  index={index}
                />
              </Reveal>
            ))}
        </div>
      </div>
    </section>
  );
}
